import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import {
  listJobs,
  getJobStats,
  getJobNames,
  getEventTypes,
  createJob,
  deleteJob,
  retryJob,
} from '../Config/jobsService';
import {
  formatEasternDateTime,
  fromDateTimeLocalInEastern,
} from '../Utils/dateTime';

const STATUSES = ['scheduled', 'running', 'completed', 'failed', 'disabled'];
const SORT_FIELDS = [
  { value: 'nextRunAt', label: 'Next run' },
  { value: 'lastRunAt', label: 'Last run' },
  { value: 'createdAt', label: 'Created' },
  { value: 'name', label: 'Name' },
];

function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [jobNames, setJobNames] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    data: '{}',
    runAt: '',
  });

  const [filters, setFilters] = useState({
    name: '',
    eventType: '',
    status: '',
    orderId: '',
    userId: '',
    userName: '',
    userMobile: '',
    search: '',
    sortBy: 'nextRunAt',
    sortOrder: 'desc',
  });

  const navigate = useNavigate();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listJobs({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });
      if (res.success) {
        setJobs(res.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.pagination?.total ?? 0,
          totalPages: res.pagination?.totalPages ?? 1,
        }));
      } else {
        toast.error(res.message || 'Failed to fetch jobs');
      }
    } catch (err) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getJobStats({
        name: filters.name || undefined,
        eventType: filters.eventType || undefined,
        orderId: filters.orderId || undefined,
        userId: filters.userId || undefined,
        userName: filters.userName || undefined,
        userMobile: filters.userMobile || undefined,
      });
      if (res.success) setStats(res.data);
    } catch {
      /* ignore */
    }
  }, [filters.name, filters.eventType, filters.orderId, filters.userId, filters.userName, filters.userMobile]);

  const fetchJobNames = useCallback(async () => {
    try {
      const res = await getJobNames();
      if (res.success) setJobNames(res.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchEventTypes = useCallback(async () => {
    try {
      const res = await getEventTypes();
      if (res.success) setEventTypes(res.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchJobNames();
  }, [fetchJobNames]);

  useEffect(() => {
    fetchEventTypes();
  }, [fetchEventTypes]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleApplyFilters = () => {
    fetchJobs();
    fetchStats();
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    let data = {};
    try {
      if (createForm.data.trim()) data = JSON.parse(createForm.data);
    } catch {
      toast.error('Invalid JSON in data field');
      return;
    }
    try {
      const body = { name: createForm.name, data };
      if (createForm.runAt.trim()) body.runAt = fromDateTimeLocalInEastern(createForm.runAt);
      const res = await createJob(body);
      if (res.success) {
        toast.success(res.message || 'Job created');
        setShowCreateModal(false);
        setCreateForm({ name: '', data: '{}', runAt: '' });
        fetchJobs();
        fetchStats();
      } else {
        toast.error(res.message || 'Failed to create job');
      }
    } catch (err) {
      toast.error('Failed to create job');
    }
  };

  const handleRetry = async (jobId, e) => {
    e.stopPropagation();
    try {
      const res = await retryJob(jobId);
      if (res.success) {
        toast.success('Job retried');
        fetchJobs();
        fetchStats();
      } else {
        toast.error(res.message || 'Failed to retry');
      }
    } catch (err) {
      toast.error('Failed to retry job');
    }
  };

  const handleDelete = async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm('Cancel and remove this job?')) return;
    try {
      const res = await deleteJob(jobId);
      if (res.success) {
        toast.success('Job canceled');
        fetchJobs();
        fetchStats();
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete job');
    }
  };

  const getStatusColor = (status) => {
    const map = {
      scheduled: 'bg-blue-100 text-blue-800',
      running: 'bg-amber-100 text-amber-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      disabled: 'bg-gray-100 text-gray-700',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (d) => formatEasternDateTime(d);

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />

      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h1 className="text-2xl font-bold">Agenda Jobs</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
            >
              Create Job
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="mb-6 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div className="rounded-lg bg-slate-50 p-3 border border-slate-200">
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="text-lg font-semibold">{stats.total ?? 0}</div>
                </div>
                <div className="rounded-lg bg-blue-50 p-3 border border-blue-200">
                  <div className="text-xs text-blue-600">Scheduled</div>
                  <div className="text-lg font-semibold">{stats.scheduled ?? 0}</div>
                </div>
                <div className="rounded-lg bg-amber-50 p-3 border border-amber-200">
                  <div className="text-xs text-amber-600">Running</div>
                  <div className="text-lg font-semibold">{stats.running ?? 0}</div>
                </div>
                <div className="rounded-lg bg-green-50 p-3 border border-green-200">
                  <div className="text-xs text-green-600">Completed</div>
                  <div className="text-lg font-semibold">{stats.completed ?? 0}</div>
                </div>
                <div className="rounded-lg bg-red-50 p-3 border border-red-200">
                  <div className="text-xs text-red-600">Failed</div>
                  <div className="text-lg font-semibold">{stats.failed ?? 0}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
                  <div className="text-xs text-gray-600">Disabled</div>
                  <div className="text-lg font-semibold">{stats.disabled ?? 0}</div>
                </div>
              </div>
              {(stats.byEventType && Object.keys(stats.byEventType).length > 0) && (
                <div className="rounded-lg bg-slate-50 p-3 border border-slate-200">
                  <div className="text-xs font-semibold text-slate-500 uppercase mb-2">By event type</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.byEventType).map(([evt, count]) => (
                      <span key={evt} className="px-2 py-1 bg-white rounded text-sm border border-slate-200">
                        {evt}: <strong>{count}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
            <input
              type="text"
              placeholder="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <select
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All names</option>
              {jobNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <select
              value={filters.eventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All events</option>
              {eventTypes.map((ev) => (
                <option key={ev} value={ev}>{ev}</option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Order ID"
              value={filters.orderId}
              onChange={(e) => handleFilterChange('orderId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="User ID"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="User name"
              value={filters.userName}
              onChange={(e) => handleFilterChange('userName', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="User mobile"
              value={filters.userMobile}
              onChange={(e) => handleFilterChange('userMobile', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              {SORT_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              Apply filters
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order/User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next run</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last run</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fails</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{job.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{job.eventType || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {(() => {
                          const payload = job.data?.payload || job.data;
                          const orderId = payload?.orderId ?? job.data?.orderId;
                          const userId = payload?.userId ?? job.data?.userId;
                          if (orderId != null) return `Order ${orderId}` + (userId ? ` · ${String(userId).slice(0, 8)}…` : '');
                          if (userId) return String(userId).slice(0, 8) + '…';
                          return '—';
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(job.nextRunAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(job.lastRunAt)}</td>
                      <td className="px-4 py-3 text-sm">{job.failCount ?? 0}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {job.status === 'failed' && (
                          <button
                            onClick={(e) => handleRetry(job.id, e)}
                            className="mr-2 px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded hover:bg-amber-200"
                          >
                            Retry
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(job.id, e)}
                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && jobs.length === 0 && (
            <div className="text-center py-8 text-gray-500">No jobs found</div>
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <span className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4">Create Job</h2>
            <form onSubmit={handleCreateJob}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job name *</label>
                  <input
                    list="job-names-list"
                    value={createForm.name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. notification.event"
                    required
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <datalist id="job-names-list">
                    {jobNames.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data (JSON)</label>
                  <textarea
                    value={createForm.data}
                    onChange={(e) => setCreateForm((f) => ({ ...f, data: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                    placeholder='{"event": "order.status.changed", "payload": {"orderId": 123, "userId": "..."}}'
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Run at (optional, Eastern time)</label>
                  <input
                    type="datetime-local"
                    value={createForm.runAt}
                    onChange={(e) => setCreateForm((f) => ({ ...f, runAt: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to run immediately</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border rounded-lg">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Jobs;
